import { FuseNavigationItem } from '@fuse/components/navigation';

/**
 * ==================================================================================================
 * @ Source of Truth
 * ==================================================================================================
 *
 * This remains the single source of truth for defining all possible navigation items.
 * To edit a link's title, icon, or path, you only need to change it here.
 * 
 * Role-based Access Control (meta.roles):
 * - If meta.roles is not defined: item is visible to all roles
 * - If meta.roles is defined: item is visible only to specified roles
 */
export const commonNavigation: FuseNavigationItem[] = [
    { id: 'dashboard', title: 'Dashboard', type: 'basic', icon: 'mat_outline:dashboard', link: '/dashboard', meta: { roles: ['Admin'] } },
    { id: 'add-order', title: 'Add Order', type: 'basic', icon: 'mat_outline:library_add', link: '/orders/new' },
    { id: 'order-list', title: 'Orders', type: 'basic', icon: 'mat_outline:list_alt', link: '/orders/list' },
    { id: 'shopify', title: 'Shopify', type: 'basic', icon: 'mat_outline:shopping_bag', link: '/shopify', meta: { roles: ['Admin', 'Production', 'Trader', 'Sales', 'Assistant'] } },
    { id: 'products', title: 'Products', type: 'basic', icon: 'mat_outline:inventory_2', link: '/products', meta: { roles: ['Admin', 'Trader', 'Sales', 'Assistant'] } },
    // { id: 'invoice', title: 'Invoice', type: 'basic', icon: 'mat_outline:receipt_long', link: '/invoice' }, // Hidden - invoicing integrated into orders page
    { id: 'shipping', title: 'Shipping', type: 'basic', icon: 'mat_outline:local_shipping', link: '/shipping', meta: { roles: ['Admin'] } },
    { id: 'delivery', title: 'Delivery', type: 'basic', icon: 'mat_outline:delivery_dining', link: '/delivery', meta: { roles: ['Admin'] } },
    { id: 'receiving', title: 'Receiving', type: 'basic', icon: 'mat_outline:inventory', link: '/receiving', meta: { roles: ['Admin'] } },
    { id: 'artwork', title: 'Artwork', type: 'basic', icon: 'mat_outline:edit', link: '/artwork', meta: { roles: ['Admin'] } },
    { id: 'purchasing', title: 'Purchasing', type: 'basic', icon: 'mat_outline:shopping_cart', link: '/purchasing', meta: { roles: ['Admin'] } },
    { id: 'analytics', title: 'Analytics', type: 'basic', icon: 'mat_outline:analytics', link: '/analytics', meta: { roles: ['Admin'] } },
    { id: 'production-plan', title: 'Production Plan', type: 'basic', icon: 'mat_outline:calendar_view_week', link: '/production-plan', meta: { roles: ['Admin'] } },
    { id: 'assignment-rules', title: 'Assignment Rules', type: 'basic', icon: 'mat_outline:rule', link: '/assignment-rules', meta: { roles: ['Admin'] } },
    {
        id: 'resources',
        title: 'Resources',
        type: 'collapsable',
        icon: 'mat_outline:group_work',
        meta: { roles: ['Admin', 'Trader', 'Sales', 'Production', 'Printer', 'Assistant'] },
        children: [
            { id: 'resources.list', title: 'Live Schedule', type: 'basic', icon: 'mat_outline:calendar_view_week', link: '/resources/list' },
            { id: 'resources.queue', title: 'Queue', type: 'basic', icon: 'mat_outline:queue', link: '/resources/queue' },
        ],
    },
    { id: 'printer-view', title: 'Printer View', type: 'basic', icon: 'mat_outline:print', link: '/printer-view', meta: { roles: ['Admin', 'Trader', 'Printer'] } },
    { id: 'warehouse-vision', title: 'Warehouse Vision', type: 'basic', icon: 'mat_outline:videocam', link: '/warehouse-vision', meta: { roles: ['Admin'] } },
    { id: 'mailbox', title: 'Mailbox', type: 'basic', icon: 'heroicons_outline:envelope', link: 'mailbox' },
    { id: 'chat', title: 'Chat', type: 'basic', icon: 'heroicons_outline:chat-bubble-bottom-center-text', link: '/chat' },
    { id: 'crm', title: 'CRM', type: 'basic', icon: 'heroicons_outline:users', link: '/crm' },
    // Direct "Customers" link for Traders (simpler than collapsible menu in horizontal nav)
    { id: 'customers', title: 'Customers', type: 'basic', icon: 'mat_outline:people_alt', link: '/user-list', meta: { roles: ['Trader', 'Sales', 'Assistant'] } },
    // Direct "Users" link for Admin in modern/horizontal layout (collapsible menu doesn't work well in horizontal nav)
    { id: 'admin-users', title: 'Users', type: 'basic', icon: 'heroicons_outline:user-group', link: '/user-list', meta: { roles: ['Admin'] } },
    {
        id   : 'users',
        title: 'Users',
        type : 'collapsable',
        icon : 'heroicons_outline:user-group',
        meta : { roles: ['Admin'] },
        children: [
/*             {
                id   : 'users.add-user',
                title: 'Add User',
                type : 'basic',
                icon : 'mat_outline:person_add_alt',
                link : '/admin/add-user'
            },  */
            {
                id   : 'user-list',
                title: 'User List',
                type : 'basic',
                icon : 'mat_outline:people_alt',
                link : '/user-list'
            },
            {
                id   : 'users.printer-list',
                title: 'Printer List',
                type : 'basic',
                icon : 'mat_outline:print',
                link : '/printer-list'
            },
            {
                id   : 'users.resource-list',
                title: 'Resource List',
                type : 'basic',
                icon : 'heroicons_outline:user-circle',
                link : '/resource-list'
            },
            {
                id   : 'users.courier-list',
                title: 'Courier List',
                type : 'basic',
                icon : 'mat_outline:local_shipping',
                link : '/courier-list'
            },
            {
                id   : 'users.assignment-rules',
                title: 'Assignment Rules',
                type : 'basic',
                icon : 'mat_outline:rule',
                link : '/assignment-rules'
            },
            {
                id   : 'users.vendor-list',
                title: 'Vendor List',
                type : 'basic',
                icon : 'heroicons_outline:building-storefront',
                link : '/vendor-list'
            },
        ]
    },
    { id: 'image-gallery', title: 'Image Gallery', type: 'basic', icon: 'mat_outline:image', link: '/image-gallery' },
    // Printers page for Traders to manage their own printers
    { id: 'trader-printers', title: 'Printers', type: 'basic', icon: 'mat_outline:print', link: '/printer-list', meta: { roles: ['Trader', 'Assistant'] } },
    // System Admin page for viewing logs and running artisan commands (Admin only)
    { id: 'system-admin', title: 'System Admin', type: 'basic', icon: 'heroicons_outline:server-stack', link: '/system-admin', meta: { roles: ['Admin'] } },
];


/**
 * ==================================================================================================
 * @ Exported Navigations
 * ==================================================================================================
 *
 * Each navigation is now a custom-built array.
 */

//  Default Navigation: Includes all items from the common source.
export const defaultNavigation: FuseNavigationItem[] = commonNavigation;

//  Compact Navigation: A smaller version created by REMOVING items.
// Here, we filter the array to only include items whose IDs are in the 'includeIds' list.
/* const compactNavIds = ['dashboard', 'chat', 'users', 'image-gallery'];
export const compactNavigation: FuseNavigationItem[] = commonNavigation.filter(item =>
    compactNavIds.includes(item.id)
); */
export const compactNavigation: FuseNavigationItem[] = commonNavigation;


// ✅ Futuristic Navigation: Contains all common items PLUS a new one.
// We use the spread syntax '...' to include all common items and then ADD a new item object.
export const futuristicNavigation: FuseNavigationItem[] = [
    ...commonNavigation,
    /* {
        id   : 'ai-tools',
        title: 'AI Tools',
        type : 'basic',
        icon : 'mat_outline:auto_awesome',
        link : '/ai-tools',
    }, */
];

//  Horizontal Navigation: A specific, hand-picked list of items.
// We use .find() to pick items by ID from the common source. This is useful for custom ordering.
// .filter(Boolean) is a safeguard to remove 'undefined' if an ID is not found.
/* export const horizontalNavigation: FuseNavigationItem[] = [
    commonNavigation.find(item => item.id === 'dashboard'),
    commonNavigation.find(item => item.id === 'order-list'),
    commonNavigation.find(item => item.id === 'invoice'),
    commonNavigation.find(item => item.id === 'users'),
].filter(Boolean) as FuseNavigationItem[]; */
 
// Horizontal navigation includes admin-level pages for role-based filtering
// Production users will see their assigned work page, others will be filtered by role
const HorizontalNavIds = [ 
    'dashboard',
    'add-order', 
    'order-list',
    'shopify',
    'products',
    // Admin-level pages (filtered by role - Production users see their assigned page)
    'shipping',
    'delivery',
    'receiving',
    'artwork',
    'purchasing',
    'analytics',
    'production-plan',
    'assignment-rules',
    'resources',
    'printer-view',
    'warehouse-vision',
    // Common pages
    'mailbox', 
    'chat',
    'crm',
    'users',        // Collapsible menu for dense/vertical layouts (Admin)
    'admin-users',  // Basic link for modern/horizontal layout (Admin)
    'customers',
    // 'image-gallery' — temporarily hidden from the modern/horizontal nav
    // while the gallery is being validated inside the orders workspace
    // (open it via the "Gallery" button on an order card). The route still
    // works directly at /image-gallery and the item is still present in
    // the default/compact/futuristic layouts.
    'trader-printers',
    'system-admin'
];
export const horizontalNavigation: FuseNavigationItem[] = commonNavigation.filter(item =>
    HorizontalNavIds.includes(item.id)
);